import { ContractSearcherContextProvider } from '@/components/contract-searcher/context'
import { Dashboard } from '@/pages/dashboard'
import { SignIn } from '@/pages/sign-in'
import { AccessControl } from '@/pages/access-control'
import { AccessControlSettings } from '@/pages/access-control/settings'
import { AccessLogsPage } from '@/pages/access-control/logs'
import AccessControlExceptionsPage, { ExceptionsListLoader } from '@/pages/access-control/exceptions'
import AccessControlExceptionsNewPage from '@/pages/access-control/exceptions/new'
import AccessControlExceptionsHistoryPage, { ExceptionsHistoryLoader } from '@/pages/access-control/exceptions/history'
import { ContractorEmployeeAccess } from '@/pages/contractor-employee-access'
import { Visit } from '@/pages/visit'
import { employeeServices, requestsServices } from '@/services'
import { store } from '@/store'
import { fetchCurrentUser } from '@/store/slices/userSlice'
import {
  Navigate,
  createBrowserRouter,
  defer,
  redirect
} from 'react-router-dom'

import {
  AssetAttachmentList,
  AssetAttachmentUpload,
  AssetCreate,
  AssetEdit,
  AssetList,
  AssetReports,
  AssetMassiveImport
} from '@/pages/assets'
import {
  AssetV2List,
  AssetV2Create,
  AssetV2Edit,
  AssetV2Reports,
  AssetV2AttachmentList,
  AssetV2MassiveImport
} from '@/pages/assets-v2'
import { AttachmentList } from '@/pages/attachments'
import {
  CompanyAttachments,
  CompanyCreate,
  CompanyEdit,
  CompanyList,
  CompanyReports
} from '@/pages/companies'
import {
  ContractAttachments,
  ContractCreate,
  ContractList,
  ContractReports
} from '@/pages/contracts'
import {
  EmployeeAttachments,
  EmployeeContractHistory,
  EmployeeCreate,
  EmployeeEdit,
  EmployeeList,
  EmployeeReports,
  EmployeeRequests,
  EmployeeMassiveImport
} from '@/pages/employees'
import { OfficeCreate, OfficeEdit, OfficeList } from '@/pages/offices'
import {
  PendingAttachmentList,
  PendingAttachmentUpload,
  PendingEmployeeContractAttachmentUpload
} from '@/pages/pending-attachments'
import { RecoveryPassword } from '@/pages/recovery-password'
import { ReferenceLibraryList } from '@/pages/reference-library'
import { UserRoleEdit, UserRoleList } from '@/pages/user_roles'
import { GroupManager } from '@/pages/user-groups'
import GroupDetail from '@/pages/user-groups/group-detail/group-detail'
import {
  UserAcceptInvitation,
  UserCreate,
  UserEdit,
  UserList,
} from '@/pages/users'
import {
  assetApi,
  attachmentApi,
  companyApi,
  contractApi,
  dashboardApi,
  employeeContractAttachmentApi,
  employeesApi,
  assetV2Api,
  permissionApi,
  userApi,
  userRoleApi,
} from '@/store/services'
import axios from 'axios'
import { Authenticated, LayoutWithError } from './components'
import {
  AssetEditLoader,
  AssetListLoader,
  CompanyEditLoader,
  CompanyListLoader,
  ContractListLoader,
  ContractLoaders,
  EmployeeListLoader,
  EmployeeLoaders,
  OfficeCreateLoader,
  OfficeEditLoader,
  OfficeListLoader,
  GroupListLoader
} from './loaders'
import { edit, list } from './utils'
import { AssetCreateAction, AssetEditAction, CompanyCreateAction, CompanyEditAction } from './actions'
import { groupApi } from '@/store/services'
import { useSelector } from 'react-redux'

// ==== Guards para rol "Control de Acceso" ====
const useIsAccessControl = () => {
  const user = useSelector((s) => s.user.current)
  const namesA = (user?.roles || []).map((r) => String(r?.name || '')).filter(Boolean)
  const namesB = Array.isArray(user?.role_names) ? user.role_names.map((n) => String(n || '')) : []
  const all = [...namesA, ...namesB].map((n) => n.toLowerCase())
  return all.includes('control de acceso')
}

const DashboardGuard = () => {
  const isAccessControl = useIsAccessControl()
  if (isAccessControl) return <Navigate to="/controldeacceso" replace />
  return <Dashboard />
}

const DefaultRedirect = () => {
  const isAccessControl = useIsAccessControl()
  return <Navigate to={isAccessControl ? '/controldeacceso' : '/dashboard'} replace />
}

const router = createBrowserRouter([
  {
    path: 'sign_in',
    element: <SignIn />
  },
  {
    path: 'recovery_password',
    element: <RecoveryPassword />
  },
  {
    path: '/users/accept_invitation',
    element: <UserAcceptInvitation />
  },
  {
    element: <Authenticated />,
    errorElement: <LayoutWithError />,
    loader: () => {
      return store.dispatch(fetchCurrentUser()).unwrap()
    },
    children: [
      {
        path: 'dashboard',
        element: <DashboardGuard />,
        loader: async ({ request }) => {
          const searchParams = new URL(request.url).searchParams
          const defaultQueryKeys = ['company_id', 'contract_id', 'office_id', 'user_id', 'submanagment_id', 'managment_id', 'dashboard_source']
          const periodQueryKeys = [...defaultQueryKeys, 'period']
          const selectedSearchParams = new URLSearchParams(
            [...searchParams.entries()].filter(([key]) => defaultQueryKeys.includes(key))
          )
          const periodSearchParams = new URLSearchParams(
            [...searchParams.entries()].filter(([key]) => periodQueryKeys.includes(key))
          )

          const dashboardItems = store.dispatch(
            dashboardApi.endpoints.getDashboardItems.initiate(`?${selectedSearchParams}`)
          ).unwrap()

          const accreditation = store.dispatch(
            dashboardApi.endpoints.getDashboardAccreditation.initiate(`?${selectedSearchParams}`)
          ).unwrap()

          const monthlyRisk = store
            .dispatch(
              dashboardApi.endpoints.getDashboardMonthlyRisk.initiate(`?${periodSearchParams}`)
            )
            .unwrap()

          const monthlyAttachments = store.dispatch(
            dashboardApi.endpoints.getDashboardMonthlyCompliance.initiate(`?${periodSearchParams}`)
          ).unwrap()

          return defer({
            dashboardItems,
            monthlyRisk,
            accreditation,
            monthlyAttachments
          })
        }
      },
      {
        path: 'offices',
        children: [
          {
            path: '',
            element: <OfficeList />,
            loader: OfficeListLoader
          },
          {
            path: 'create',
            element: <OfficeCreate />,
            loader: OfficeCreateLoader
          },
          {
            path: 'edit/:id',
            element: <OfficeEdit />,
            loader: OfficeEditLoader
          }
        ]
      },
      {
        path: 'companies',
        children: [
          {
            path: '',
            element: <CompanyList />,
            loader: CompanyListLoader,
            children: [
              {
                path: 'reports',
                element: <CompanyReports />,
                loader: () => axios.get('/api/v1/reports/company_columns')
              },
              {
                path: ':id/attachments',
                element: <CompanyAttachments />,
                loader: ({ params: { id } }) =>
                  store.dispatch(companyApi.endpoints.getCompany.initiate(id)).unwrap()
              },
              {
                path: ':id/attachments/:attachmentId/upload_attachment',
                element: <PendingAttachmentUpload />,
                loader: ({ params: { attachmentId } }) =>
                  store.dispatch(attachmentApi.endpoints.getAttachment.initiate(attachmentId)).unwrap()
              }
            ]
          },
          {
            path: 'create',
            element: <CompanyCreate />,
            action: CompanyCreateAction
          }
        ]
      },
      {
        path: 'companies/edit/:id',
        element: <CompanyEdit />,
        action: CompanyEditAction,
        loader: CompanyEditLoader
      },
      {
        path: 'contracts',
        children: [
          {
            path: '',
            element: <ContractList />,
            loader: ContractListLoader,
            children: [
              {
                path: 'reports',
                element: <ContractReports />,
                loader: async () => {
                  const [contractColumnsResponse, contractSummaryColumnsResponse] = await Promise.all([
                    axios.get('/api/v1/reports/contract_columns'),
                    axios.get('/api/v1/reports/contract_summary_columns').catch((error) => {
                      if (error.response?.status === 403) {
                        return { data: [] }
                      }

                      throw error
                    })
                  ])

                  return {
                    contractColumns: contractColumnsResponse.data,
                    contractSummaryColumns: contractSummaryColumnsResponse.data
                  }
                }
              },
              {
                path: ':id/attachments/:attachmentId/upload_attachment',
                element: <PendingAttachmentUpload />,
                loader: ({ params: { attachmentId } }) =>
                  store.dispatch(attachmentApi.endpoints.getAttachment.initiate(attachmentId)).unwrap()
              },
              {
                path: ':id/attachments/list',
                element: <ContractAttachments />,
                loader: ({ params: { id } }) =>
                  store.dispatch(contractApi.endpoints.getContract.initiate(id)).unwrap()
              }
            ]
          }
        ]
      },
      {
        path: 'contracts/new',
        element: <ContractCreate />,
        loader: () => ContractLoaders.create()
      },
      {
        path: 'contracts/:id',
        element: <ContractCreate editMode />,
        loader: (data) => ContractLoaders.edit(data)
      },
      {
        path: 'employees',
        children: [
          {
            path: '',
            element: (
              <ContractSearcherContextProvider>
                <EmployeeList />
              </ContractSearcherContextProvider>
            ),
            loader: EmployeeListLoader,
            children: [
              {
                path: 'reports',
                element: <EmployeeReports />,
                loader: () => axios.get('/api/v1/reports/employee_columns')
              },
              {
                path: ':id/requests',
                element: <EmployeeRequests />,
                loader: ({ params }) =>
                  Promise.all([
                    store.dispatch(
                      employeesApi.endpoints.getEmployee.initiate({ id: params.id })
                    ).unwrap(),
                    employeeServices.getRoles(),
                    requestsServices.getPendingRequests(params.id)
                  ])
              },
              {
                path: ':id/attachments',
                element: <EmployeeAttachments />,
                loader: (data) => EmployeeLoaders.attachments(data)
              },
              {
                path: ':id/attachments/:attachmentId/upload_employee_contract_attachment',
                element: <PendingEmployeeContractAttachmentUpload />
              },
              {
                path: ':id/contract_history',
                element: <EmployeeContractHistory />,
                loader: ({ params: { id } }) =>
                  store.dispatch(
                    employeesApi.endpoints.getEmployee.initiate({ id })
                  ).unwrap()
              }
            ]
          },
          {
            path: 'create',
            element: <EmployeeCreate />,
            action: async ({ request }) => {
              const formData = await request.formData()
              const params = Object.fromEntries(formData)
              try {
                const response = await fetch('/api/v1/employees/temp_create', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ employee: params })
                })
                const { temp_hash } = await response.json()
                return redirect(`/employees/new?temp_hash=${temp_hash}`)
              } catch (error) {
                return error
              }
            },
            loader: async () => {
              const countries = await EmployeeLoaders.countries()
              return { countriesData: { data: { countries } } }
            }
          },
          {
            path: 'new',
            element: (
              <ContractSearcherContextProvider>
                <EmployeeEdit mode="create" />
              </ContractSearcherContextProvider>
            ),
            loader: async () => {
              try {
                const countriesResponse = await EmployeeLoaders.countries()
                return {
                  countriesData: { data: { countries: countriesResponse } },
                  tempHash: new URLSearchParams(window.location.search).get('temp_hash')
                }
              } catch (error) {
                return {
                  countriesData: { data: { countries: [] } },
                  tempHash: null
                }
              }
            }
          },
          {
            path: 'edit/:id',
            element: (
              <ContractSearcherContextProvider>
                <EmployeeEdit mode="edit" />
              </ContractSearcherContextProvider>
            ),
            action: async ({ request }) => {
              const params = await request.json()
              try {
                await store.dispatch(
                  employeesApi.endpoints.updateEmployee.initiate(params)
                ).unwrap()
                return redirect(list('employees'))
              } catch (error) {
                return error
              }
            },
            loader: (data) => EmployeeLoaders.edit(data)
          },
          {
            path: 'import',
            element: <EmployeeMassiveImport />
          }
        ]
      },
      {
        path: 'assets',
        children: [
          {
            path: '',
            element: <AssetList />,
            loader: AssetListLoader,
            children: [
              {
                path: 'reports',
                element: <AssetReports />,
                loader: () => axios.get('/api/v1/reports/asset_columns')
              },
              {
                path: ':id/attachments',
                element: <AssetAttachmentList />,
                loader: ({ params: { id } }) =>
                  store.dispatch(assetApi.endpoints.getAsset.initiate(id)).unwrap()
              },
              {
                path: ':id/attachments/upload',
                element: <AssetAttachmentUpload />,
                loader: ({ params: { id } }) =>
                  store.dispatch(assetApi.endpoints.getAsset.initiate(id)).unwrap()
              },
              {
                path: ':id/attachments/:attachmentId/upload_attachment',
                element: <PendingAttachmentUpload />,
                loader: ({ params: { attachmentId } }) =>
                  store.dispatch(attachmentApi.endpoints.getAttachment.initiate(attachmentId)).unwrap()
              }
            ]
          },
          {
            path: 'create',
            element: <AssetCreate />,
            action: AssetCreateAction
          },
          {
            path: 'edit/:id',
            element: <AssetEdit />,
            loader: AssetEditLoader,
            action: AssetEditAction
          },
          {
            path: 'import',
            element: <AssetMassiveImport />
          }
        ]
      },
      {
        path: 'assets_v2',
        children: [
          {
            path: '',
            element: <AssetV2List />,
            children: [
              {
                path: 'reports',
                element: <AssetV2Reports />,
                loader: ({ request }) => {
                  const url = new URL(request.url)
                  const assetTypeId = url.searchParams.get('asset_type_id')
                  const assetCategoryId = url.searchParams.get('asset_category_id')
                  let params = ''
                  if (assetTypeId) {
                    params = `?asset_type_id=${assetTypeId}`
                  } else if (assetCategoryId) {
                    params = `?asset_category_id=${assetCategoryId}`
                  }
                  return axios.get(`/api/v1/reports/asset_v2_columns${params}`)
                }
              },
              {
                path: ':id/attachments',
                element: <AssetV2AttachmentList />,
                loader: ({ params: { id } }) =>
                  store.dispatch(assetV2Api.endpoints.getAssetV2.initiate(id)).unwrap()
              },
              {
                path: ':id/attachments/:attachmentId/upload_attachment',
                element: <PendingAttachmentUpload />,
                loader: ({ params: { attachmentId } }) =>
                  store.dispatch(attachmentApi.endpoints.getAttachment.initiate(attachmentId)).unwrap()
              }
            ]
          },
          {
            path: 'create',
            element: <AssetV2Create />
          },
          {
            path: 'edit/:id',
            element: <AssetV2Edit />
          },
          {
            path: 'import',
            element: <AssetV2MassiveImport />
          }
        ]
      },
      {
        path: 'attachments',
        element: <AttachmentList />,
        children: [
          {
            path: ':id/upload_attachments',
            element: <PendingAttachmentUpload />,
            loader: ({ params: { id } }) =>
              store.dispatch(attachmentApi.endpoints.getAttachment.initiate(id)).unwrap()
          },
          {
            path: ':id/upload_employee_contract_attachment',
            element: <PendingEmployeeContractAttachmentUpload />,
            loader: ({ params: { id } }) =>
              store.dispatch(
                employeeContractAttachmentApi.endpoints.getEmployeeContractAttachment.initiate(id)
              ).unwrap()
          }
        ]
      },
      {
        path: 'reference-library',
        element: <ReferenceLibraryList />
      },
      {
        path: 'users',
        children: [
          {
            path: '',
            element: <UserList />,
            loader: (args) => {
              const url = new URL(args.request.url)
              const searchParams = url.searchParams

              if (!searchParams.get('page') || !searchParams.get('per_page')) {
                const newSearchParams = new URLSearchParams([
                  ['page', '1'],
                  ['per_page', '10']
                ])
                return redirect(`/users?${newSearchParams}`)
              }

              return defer({
                promise: store.dispatch(userApi.endpoints.getUsers.initiate(`?${searchParams}`)).unwrap()
              })
            }
          },
          {
            path: 'edit/:id',
            element: <UserEdit />,
            loader: ({ params: { id } }) =>
              store.dispatch(userApi.endpoints.getUser.initiate(id, { forceRefetch: true })).unwrap()
          },
          {
            path: 'create',
            element: <UserCreate />
          },
          {
            path: 'groups',
            children: [
              {
                path: '',
                element: <GroupManager />
              },
              {
                path: 'create',
                element: <GroupDetail />
              },
              {
                path: 'edit/:id',
                element: <GroupDetail />,
                loader: ({ params: { id } }) =>
                  store.dispatch(groupApi.endpoints.getGroup.initiate(id)).unwrap()
              }
            ]
          }
        ]
      },
      {
        path: 'user_roles',
        element: <UserRoleList />,
        loader: () => {
          const roles = store.dispatch(
            userRoleApi.endpoints.getUserRoles.initiate('?page=1&per_page=20')
          ).unwrap()
          return roles
        },
        children: [
          {
            path: 'edit/:id',
            element: <UserRoleEdit />,
            loader: ({ params: { id } }) => {
              const role = store.dispatch(
                userRoleApi.endpoints.getUserRole.initiate(id, { forceRefetch: true })
              ).unwrap()

              const availableActions = store.dispatch(
                permissionApi.endpoints.getAvailableActions.initiate()
              ).unwrap()

              return Promise.all([role, availableActions])
            }
          }
        ]
      },
      {
        path: 'controldeacceso',
        children: [
          {
            path: '',
            element: <AccessControl />
          },
          {
            path: 'settings',
            element: <AccessControlSettings />
          },
          {
            path: 'logs',
            element: <AccessLogsPage />
          },
          {
            path: 'exceptions',
            element: <AccessControlExceptionsPage />,
            loader: ExceptionsListLoader
          },
          {
            path: 'exceptions/new',
            element: <AccessControlExceptionsNewPage />
          },
          {
            path: 'exceptions/history',
            element: <AccessControlExceptionsHistoryPage />,
            loader: ExceptionsHistoryLoader
          }
        ]
      },
      {
        path: 'compliance-query',
        element: <ContractorEmployeeAccess />
      },
      {
        path: '/visits',
        element: <Visit />,
      }
    ]
  },
  {
    path: '*',
    element: <DefaultRedirect />
  }
])

export default router
